import { useChatStore } from '@/store/chatStore'
import type { AvailableSlot } from '@/types'
import {
  getAvailableSlots,
  bookAppointment,
  checkReschedule,
  rescheduleAppointment,
} from '@/services/appointmentApi'

// ─────────────────────────────────────────────────────────────────────────────
// useAppointmentBooking
// Drives the multi-step appointment questionnaire.
// Called from ChatPanel — handles input at each step and slot selection.
//
// Reschedule detection:
//   After the customer enters their email, we call check-reschedule with
//   email + WhatsApp. If a future non-cancelled appointment exists we store
//   the leadId and show the existing slot label — the slot-selection screen
//   becomes a reschedule screen and we call reschedule instead of book.
// ─────────────────────────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_REGEX = /^\+[0-9]{7,15}$/

export function useAppointmentBooking() {
  const config             = useChatStore((s) => s.config)
  const bookingStep        = useChatStore((s) => s.bookingStep)
  const bookingData        = useChatStore((s) => s.bookingData)
  const availableSlots     = useChatStore((s) => s.availableSlots)
  const rescheduleLeadId   = useChatStore((s) => s.rescheduleLeadId)
  const existingBookingInfo = useChatStore((s) => s.existingBookingInfo)

  const advanceBooking        = useChatStore((s) => s.advanceBooking)
  const cancelBooking         = useChatStore((s) => s.cancelBooking)
  const setAvailableSlots     = useChatStore((s) => s.setAvailableSlots)
  const setRescheduleLeadId   = useChatStore((s) => s.setRescheduleLeadId)
  const setExistingBookingInfo = useChatStore((s) => s.setExistingBookingInfo)

  // Prompt text shown for each step
  const stepPrompt: Record<string, string> = {
    name:     'What\'s your full name?',
    whatsapp: 'Your WhatsApp number with country code (e.g. +971501234567):',
    email:    'Your email address:',
    slot:     availableSlots.length === 0
      ? 'Fetching available slots…'
      : rescheduleLeadId
        ? `You already have an appointment: ${existingBookingInfo}. Select a new slot to reschedule it:`
        : 'Choose a time slot:',
  }

  // Called by ChatPanel's text input on Enter/Send
  async function handleTextInput(value: string): Promise<string | null> {
    const trimmed = value.trim()
    if (!trimmed) return null

    if (bookingStep === 'name') {
      if (trimmed.length < 2) return 'Please enter your full name.'
      advanceBooking('name', trimmed)
      return null
    }

    if (bookingStep === 'whatsapp') {
      if (!PHONE_REGEX.test(trimmed))
        return 'Please enter a valid WhatsApp number starting with + (e.g. +971501234567).'
      advanceBooking('whatsapp', trimmed)
      return null
    }

    if (bookingStep === 'email') {
      if (!EMAIL_REGEX.test(trimmed)) return 'Please enter a valid email address.'

      // Advance step to 'slot' immediately so the UI moves on
      advanceBooking('email', trimmed)

      if (config?.domainId) {
        // Run check-reschedule and slot fetch in parallel
        const [rescheduleResult, slots] = await Promise.allSettled([
          checkReschedule(config.domainId, trimmed, bookingData.whatsapp),
          getAvailableSlots(config.domainId),
        ])

        // Store reschedule info if an existing appointment was found
        if (rescheduleResult.status === 'fulfilled' && rescheduleResult.value.hasExisting) {
          setRescheduleLeadId(rescheduleResult.value.leadId)
          setExistingBookingInfo(rescheduleResult.value.existingSlotLabel)
        } else {
          setRescheduleLeadId(null)
          setExistingBookingInfo(null)
        }

        setAvailableSlots(slots.status === 'fulfilled' ? slots.value : [])
      }
      return null
    }

    return null
  }

  // Called when user taps a slot chip
  async function handleSlotSelect(slot: AvailableSlot): Promise<{ success: boolean; error?: string }> {
    if (!config?.domainId) return { success: false, error: 'Configuration error.' }

    try {
      if (rescheduleLeadId) {
        // Customer already has an appointment — update it instead of creating a new one
        await rescheduleAppointment(
          config.domainId,
          rescheduleLeadId,
          bookingData.email,
          bookingData.whatsapp,
          slot.date,
          slot.slotStart,
          slot.slotEnd,
        )
      } else {
        await bookAppointment(
          config.domainId,
          bookingData.name,
          bookingData.whatsapp,
          bookingData.email,
          slot.date,
          slot.slotStart,
          slot.slotEnd,
          useChatStore.getState().currentSessionId,
        )
      }
      cancelBooking()
      return { success: true }
    } catch (e: any) {
      const msg = e?.response?.data?.error
      if (e?.response?.status === 409) {
        // Slot just got taken — refresh the list
        try {
          const fresh = await getAvailableSlots(config.domainId)
          setAvailableSlots(fresh)
        } catch {}
        return { success: false, error: 'That slot was just taken. Please pick another.' }
      }
      if (e?.response?.status === 403)
        return { success: false, error: 'Identity mismatch. Please re-enter your details.' }
      return { success: false, error: msg ?? 'Booking failed. Please try again.' }
    }
  }

  return {
    bookingStep,
    bookingData,
    availableSlots,
    rescheduleLeadId,
    existingBookingInfo,
    stepPrompt: bookingStep ? stepPrompt[bookingStep] ?? '' : '',
    handleTextInput,
    handleSlotSelect,
    cancelBooking,
  }
}
